import { memo, useMemo } from 'react';

import { CheckIcon } from '@air/design-icons';
import { Button, type ButtonProps } from '@air/primitive-button';
import { DropdownMenu, DropdownMenuItem, DropdownMenuLabel } from '@air/primitive-dropdown-menu';

import { PLAYBACK_RATES } from '../constants/playbackRate';
import { usePlayerPlaybackChange } from '../hooks/usePlayerPlaybackChange';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerPlaybackRateButtonProps = Omit<ButtonProps, 'children' | 'label'>;

export const PlayerPlaybackRateButton = memo((props: PlayerPlaybackRateButtonProps) => {
  const playbackRate = usePlayerStore((state) => state.playbackRate);
  const { onPlayerPlaybackChange: handlePlaybackRateChange } = usePlayerPlaybackChange();
  const speedOptions = useMemo(
    () => PLAYBACK_RATES.map((speed) => ({ onSelect: () => handlePlaybackRateChange(speed), speed })),
    [handlePlaybackRateChange],
  );

  const isDefaultPlaybackRate = playbackRate === 1;

  return (
    <DropdownMenu
      side="top"
      trigger={
        <Button
          appearance="ghost"
          color={isDefaultPlaybackRate ? 'grey' : 'blue'}
          data-testid="PLAYER_PLAYBACK_RATE_BUTTON"
          {...props}
        >
          {playbackRate}×
        </Button>
      }
    >
      <DropdownMenuLabel>Speed</DropdownMenuLabel>
      {speedOptions.map(({ speed, onSelect }) => (
        <DropdownMenuItem
          data-id={speed}
          key={speed}
          label={`${speed}×`}
          onSelect={onSelect}
          suffix={speed === playbackRate ? <CheckIcon size="sm" /> : null}
        />
      ))}
    </DropdownMenu>
  );
});

PlayerPlaybackRateButton.displayName = 'PlayerPlaybackRateButton';
